"use client";

import Image from "next/image";
import { useState } from "react";
import { screenThumb } from "@/lib/firstparty/thumbs";
import { cn } from "@/lib/utils";

/**
 * A captured screen filling its (relative) box. It loads the stored WebP copies directly; a screen
 * with no copy yet, or one stored elsewhere, goes through next/image.
 */
export function ScreenImage({
  src,
  sizes,
  alt = "",
  priority = false,
  draggable,
  className,
}: {
  src: string;
  sizes: string;
  alt?: string;
  priority?: boolean;
  draggable?: boolean;
  className?: string;
}) {
  const [missing, setMissing] = useState(false);
  const sources = screenSources(src);
  if (!sources || missing) {
    return <Image src={src} alt={alt} fill sizes={sizes} priority={priority} draggable={draggable} className={className} />;
  }
  return (
    // oxlint-disable-next-line nextjs/no-img-element -- stored WebP copies, already sized for these slots
    <img
      src={sources.src}
      srcSet={sources.srcSet}
      sizes={sizes}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      decoding="async"
      draggable={draggable}
      onError={() => setMissing(true)}
      // A copy that failed before hydration fired its error with no listener attached yet.
      ref={(img) => {
        if (img?.complete && img.naturalWidth === 0) setMissing(true);
      }}
      className={cn("absolute inset-0 h-full w-full", className)}
    />
  );
}

/** The stored WebP copies of a screen as img sources, or null when it has none. */
function screenSources(src: string) {
  const small = screenThumb(src, 400);
  const large = screenThumb(src, 800);
  return small && large ? { src: small, srcSet: `${small} 400w, ${large} 800w` } : null;
}

const ready = new Map<string, Promise<boolean>>();

/**
 * Loads and decodes the copy a ScreenImage with these sizes will pick, so it paints at once when it
 * mounts. Resolves false when the screen has no copy or the copy fails to load.
 */
export function preloadScreen(src: string, sizes: string): Promise<boolean> {
  const key = `${sizes}|${src}`;
  const known = ready.get(key);
  if (known) return known;
  const sources = typeof window === "undefined" ? null : screenSources(src);
  if (!sources) return Promise.resolve(false);
  const img = new window.Image();
  img.decoding = "async";
  img.sizes = sizes;
  img.srcset = sources.srcSet;
  img.src = sources.src;
  const done = img.decode().then(
    () => true,
    () => img.complete && img.naturalWidth > 0,
  );
  ready.set(key, done);
  void done.then((ok) => {
    if (!ok) ready.delete(key);
  });
  return done;
}

